'use client';

import Link from 'next/link';
import { ChevronRight, DoorClosed, ExternalLink, LucideIcon, User, UserPlus } from 'lucide-react';
import { useAuthLogout } from '@/hooks/useAuthLogout';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { SERVICE_AUTH_ME_ITEMS, SERVICE_SUPPORT_ITEMS } from '@/constants/constants';
import BackgroundSelector from '@/components/BackgroundSelector';
import IconAdaptive from '@/components/IconAdaptive';
import SectionTitle from '@/components/SectionTitle';
import DashboardAchievementDataUnit from '@/components/features/dashboard/unit/DashboardAchievementDataUnit';
import DashboardActivityDataUnit from '@/components/features/dashboard/unit/DashboardActivityDataUnit';
import DashboardPersonalChipUnit from '@/components/features/dashboard/unit/DashboardPersonalChipUnit';
import DashboardProfileUnit from '@/components/features/dashboard/unit/DashboardProfileUnit';
import DashboardScoreUnit from '@/components/features/dashboard/unit/DashboardScoreUnit';

const MENU_ITEM_CLASS =
    'flex items-center h-12 px-3 rounded-xl border-[1.5px] border-color-co-gray hover:border-color-service-mouseover transition-colors duration-200 cursor-pointer group';

export default function HeaderSlideMenu() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

    return isAuthenticated ? <HeaderSlideMenuAuthenticated /> : <HeaderSlideMenuGuest />;
}

function HeaderSlideMenuAuthenticated() {
    const { safePush } = useSafeNavigation();
    const { scheme } = useThemeColor();
    const { logout } = useAuthLogout();
    const { closeAllModalSlides } = useModalSlideStore();

    const handleNavigate = (path: string) => {
        safePush(path);
        closeAllModalSlides();
    };

    const handleLogout = async () => {
        await logout();
        await safePush('/');
        closeAllModalSlides();
    };

    return (
        <div className="flex flex-col items-center w-full h-full bg-color-white-base overflow-y-auto overflow-x-hidden pt-4 pb-12">
            <div className="flex flex-col w-[96%] xl:w-[94%] max-w-[1280px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[auto_auto_1fr] gap-x-6 xl:gap-x-8 gap-y-8 md:gap-y-10 w-full">
                    <div className={`lg:col-start-1 lg:row-start-1 rounded-xl overflow-hidden ${scheme?.bg ?? ''}`}>
                        <div className="w-full bg-color-white-base/80">
                            <DashboardProfileUnit />
                        </div>
                    </div>

                    <div className="lg:col-start-2 lg:row-start-1 lg:row-span-3 lg:self-start grid grid-cols-2 lg:grid-cols-1 gap-x-2 md:gap-x-4 gap-y-8 w-full">
                        <PortfolioSection
                            className="flex flex-col w-full"
                            itemsClassName="flex flex-col gap-2 w-full pt-4"
                            onNavigate={handleNavigate}
                        />
                        <div className="flex flex-col w-full">
                            <SupportSection
                                className="flex flex-col w-full"
                                itemsClassName="flex flex-col gap-2 w-full pt-4"
                            />
                            <div className="flex flex-col w-full mt-10">
                                <SlideMenuButton
                                    Icon={DoorClosed}
                                    label="LOGOUT."
                                    jp="ログアウト"
                                    onClick={handleLogout}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-start-1 lg:row-start-2 flex flex-col w-full">
                        <SectionTitle title={`Account.`} />
                        <div className="flex w-full pt-4">
                            <DashboardPersonalChipUnit />
                        </div>
                    </div>

                    <ColorSection className="lg:col-start-1 lg:row-start-3 lg:self-start flex flex-col w-full" />
                </div>

                <div className="flex flex-col w-full rounded-2xl bg-color-co-gray-light px-4 py-5 md:px-6 md:py-6 mt-10 md:mt-14">
                    <SectionTitle title={`Activity & Achievement.`} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 w-full pt-5">
                        <div className="flex flex-col gap-y-2 w-full">
                            <DashboardScoreUnit />
                            <DashboardActivityDataUnit />
                        </div>
                        <DashboardAchievementDataUnit />
                    </div>
                </div>
            </div>
        </div>
    );
}

function HeaderSlideMenuGuest() {
    const { safePush } = useSafeNavigation();
    const { scheme } = useThemeColor();
    const { closeAllModalSlides } = useModalSlideStore();

    const handleNavigate = (path: string) => {
        safePush(path);
        closeAllModalSlides();
    };

    return (
        <div className="flex w-full h-full bg-color-white-base overflow-hidden">
            <div className={`hidden md:block flex-none md:w-[20%] h-full bg-svg ${scheme?.bg ?? ''}`}></div>

            <div className="flex flex-col items-center flex-1 min-w-0 h-full bg-color-white-base overflow-y-auto overflow-x-hidden pt-4 pb-0 md:pb-12">
                <div className="flex flex-col w-[90%] md:w-[86%] max-w-[880px]">
                    <div className="grid grid-cols-2 gap-x-2 md:gap-x-4 w-full py-6">
                        <SlideMenuButton
                            Icon={User}
                            label="LOGIN."
                            jp="ログイン"
                            onClick={() => handleNavigate('/auth/login')}
                        />
                        <SlideMenuButton
                            Icon={UserPlus}
                            label="SIGN UP."
                            jp="アカウント作成"
                            onClick={() => handleNavigate('/auth/signup')}
                        />
                    </div>

                    <div className="flex flex-col w-full mt-8">
                        <div className="grid grid-cols-2 gap-x-2 md:gap-x-4 w-full">
                            <PortfolioSection className="flex flex-col w-full" onNavigate={handleNavigate} />
                            <SupportSection className="flex flex-col w-full" />
                        </div>

                        <ColorSection className="flex flex-col w-full mt-8" />
                    </div>
                </div>

                <div className={`md:hidden w-full flex-1 min-h-28 mt-4 bg-svg ${scheme?.bg ?? ''}`}></div>
            </div>
        </div>
    );
}

function SlideMenuButton({
    Icon,
    label,
    jp,
    onClick,
}: {
    Icon: LucideIcon;
    label: string;
    jp: string;
    onClick: () => void;
}) {
    return (
        <div className={MENU_ITEM_CLASS} onClick={onClick}>
            <div className="flex items-center justify-center">
                <Icon
                    className="transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover"
                    strokeWidth={2.25}
                    size={24}
                />
            </div>
            <div className="flex flex-col md:flex-row font-black pl-2 min-w-0">
                <div className="flex font-montserrat font-black text-[11px] md:text-sm whitespace-nowrap tracking-wide md:pr-1 transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                    {label}
                </div>
                <div className="flex font-corp font-bold text-[10px] md:text-xs whitespace-nowrap tracking-wide md:pt-[2px] transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                    {jp}
                </div>
            </div>
        </div>
    );
}

function PortfolioSection({
    className,
    itemsClassName = 'flex flex-col w-full pt-4 md:pb-4 gap-2',
    onNavigate,
}: {
    className: string;
    itemsClassName?: string;
    onNavigate: (path: string) => void;
}) {
    return (
        <div className={className}>
            <SectionTitle title={`Portfolio.`} />
            <div className={itemsClassName}>
                {SERVICE_AUTH_ME_ITEMS.map(({ label, jp, path, Icon }) => (
                    <div key={path} className={MENU_ITEM_CLASS} onClick={() => onNavigate(path)}>
                        <div className="flex items-center justify-center">
                            <Icon
                                className="stroke-width-2.25 size-18 transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover"
                                strokeWidth={2.25}
                                size={24}
                            />
                        </div>
                        <div className="flex flex-col md:flex-row font-black pl-2 min-w-0">
                            <div className="flex font-montserrat font-black text-[11px] md:text-sm whitespace-nowrap tracking-wide md:pr-1 transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                                {label}
                            </div>
                            <div className="flex font-corp font-bold text-[10px] md:text-xs whitespace-nowrap tracking-wide md:pt-[2px] transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                                {jp}
                            </div>
                        </div>
                        <div className="flex items-center justify-center ml-auto pl-1">
                            <IconAdaptive
                                Icon={ChevronRight}
                                strokeWidth={2.5}
                                baseSize={16}
                                className="text-gray-300"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SupportSection({
    className,
    itemsClassName = 'flex flex-col w-full pt-4 md:pb-4 gap-2',
}: {
    className: string;
    itemsClassName?: string;
}) {
    return (
        <div className={className}>
            <SectionTitle title={`Support.`} />
            <div className={itemsClassName}>
                {SERVICE_SUPPORT_ITEMS.map(({ label, jp, path, Icon }) => (
                    <Link key={path} href={path} target="_blank" rel="noopener noreferrer">
                        <div className={MENU_ITEM_CLASS}>
                            <div className="flex items-center justify-center">
                                <Icon
                                    className="stroke-width-2.25 size-18 transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover"
                                    strokeWidth={2.25}
                                    size={24}
                                />
                            </div>
                            <div className="flex flex-col md:flex-row font-black pl-2 min-w-0">
                                <div className="flex font-montserrat font-black text-[11px] md:text-sm whitespace-nowrap tracking-wide md:pr-1 transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                                    {label}
                                </div>
                                <div className="flex font-corp font-bold text-[10px] md:text-xs whitespace-nowrap tracking-wide md:pt-[2px] transition-colors duration-200 text-color-base group-hover:text-color-service-mouseover">
                                    {jp}
                                </div>
                            </div>
                            <div className="flex items-center justify-center ml-auto pl-1">
                                <IconAdaptive
                                    Icon={ExternalLink}
                                    strokeWidth={2.5}
                                    baseSize={16}
                                    className="text-gray-300"
                                />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ColorSection({ className }: { className: string }) {
    return (
        <div className={className}>
            <SectionTitle title={`Color.`} />
            <div className="flex w-full py-4">
                <BackgroundSelector />
            </div>
        </div>
    );
}
